<template>
  <div class="container-fluid d-flex align-items-center justify-content-center vh-100">
    <div class="w-100">
    <div class="row justify-content-center">
      <div class="col-3 my-3 text-center">
        <img width="300" alt="Health Here" src="../assets/health-here-logo.svg"/>
      </div>
    </div>
    <div class="col">
      <b-alert v-if="generalErrorMessage" :show="true" fade variant="danger">
        {{ generalErrorMessage }}
      </b-alert>
    </div>
    <div class="row justify-content-center">
      <div class="col-3">
        <b-card v-if="!showRecoveryCodes">

          <h4>Set up two-factor authentication</h4>

          <p>Scan this QR code with your authenticator app.</p>

          <p class="text-muted">Use Google Authenticator, Microsoft Authenticator, or a similar app.</p>

          <div v-if="qrCodeBase64" class="text-center mb-4">
            <img :src="qrCodeBase64"
                 alt="Scan QR Code"
                 style="width: 240px; height: 240px;" />
          </div>

          <div v-if="manualSecret" class="mb-4">
            <p class="mt-3 mb-1">
              Can’t scan the code? Enter it manually using this key:
            </p>
            <code>{{ manualSecret }}</code>
          </div>

          <b-form class="mt-3">
            <b-form-group :state="stateForField('code')" :invalid-feedback="errorForField('code')" :label="'6-digit code'">
              <div class="d-flex justify-content-between">
                <input
                    v-for="(digit, index) in codeArray"
                    :key="index"
                    autofocus
                    ref="codeInputs"
                    class="form-control text-center mx-1 code-input"
                    maxlength="1"
                    v-model="codeArray[index]"
                    @input="onInput(index)"
                    @keydown.backspace="onBackspace(index)"
                />
              </div>
            </b-form-group>
          </b-form>
            <button type="submit" @click.prevent="submit()" class="btn btn-orange btn-block">Verify</button>
        </b-card>

      <div v-if="showRecoveryCodes" class="mt-4">
        <b-card v-if="showRecoveryCodes">
          <h4 class="mb-2">Save your recovery codes</h4>

          <p class="mb-3">
            If you lose access to your authenticator app, you can use one of these codes to sign in.
          </p>

          <p class="text-muted mb-4">These codes can be used to access your account if you lose your authenticator device. Each code can only be used once. Store them somewhere safe.</p>

          <div class="recovery-codes-box p-3 mb-3">
            <div class="row">
              <div
                v-for="(code, index) in recoveryCodes"
                :key="index"
                class="col-6 mb-2">
                <code>{{ code }}</code>
              </div>
            </div>
          </div>
          <button type="submit" @click.prevent="finishSetup()" class="btn btn-orange btn-block">Confirm</button>
        </b-card>
      </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import api from '@/services/api'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'

  export default {
    name: 'TwoFactorSetup',
    mixins: [ErrorsMixin, AnalyticsMixin],
    props: {
      token: {
        type: String,
        required: false
      }
    },

    data () {
      return {
        qrCodeBase64: '',
        manualSecret: '',
        otpauthUri: '',
        showRecoveryCodes: false,
        recoveryCodes: {},
        codeArray: ['', '', '', '', '', ''],
        mfaSetupStartTime: null
      }
    },
    computed: {
      finalCode () {
        return this.codeArray.join('')
      }
    },
    methods: {
      onInput (index) {
        const value = this.codeArray[index]
        this.codeArray[index] = value.replace(/[^0-9]/g, '')
        if (value && index < 5) {
          this.$refs.codeInputs[index + 1].focus()
        }
      },

      onBackspace (index) {
        if (!this.codeArray[index] && index > 0) {
          this.$refs.codeInputs[index - 1].focus()
        }
      },

      validate () {
        this.clearAllErrors()

        if (!this.finalCode) {
          this.addErrorForField('code', 'Code is required')
        }
        return !this.hasErrors()
      },
      loadData () {
        this.isLoading = true
        api.post('/Auth/TwoFactor/Setup/Begin', { pendingToken: this.token }).then(response => {
          this.isLoading = false
          this.qrCodeBase64 = response.data.qrCodeBase64
          this.otpauthUri = response.data.otpauthUri
          const url = new URL(this.otpauthUri.replace('otpauth://totp/', 'http://dummy/'))
          this.manualSecret = url.searchParams.get('secret') || ''
          this.mfaSetupStartTime = Date.now()
          this.startMfaExpirationTimer()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('TwoFactorSetup', 'LoadError', error.message)
            this.$log.error('Error attempting two factor setup: ' + error.message, error)
            this.setFromApiErrorResponse(error)
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },
      startMfaExpirationTimer () {
        const tenMinutes = 10 * 60 * 1000
        if (this.mfaTimer) {
          clearTimeout(this.mfaTimer)
        }
        this.mfaTimer = setTimeout(() => {
          this.handleMfaExpired()
        }, tenMinutes)
      },
      clearMfaTimer () {
        if (this.mfaTimer) {
          clearTimeout(this.mfaTimer)
          this.mfaTimer = null
        }
      },
      handleMfaExpired () {
        if (this.mfaTimer) {
          clearTimeout(this.mfaTimer)
        }
        this.mfaSetupStartTime = null
        this.$router.push({
          name: 'login',
          query: { reason: 'mfaExpired' }
        })
      },
      submit () {
        this.clearMfaTimer()
        if (this.isSaving) return

        if (!this.validate()) {
          return
        }
        this.isSaving = true
        this.clearAllErrors()

        const parameters = {
          pendingToken: this.token,
          totpCode: this.finalCode
        }
        api.post('/Auth/TwoFactor/Setup/Confirm', parameters).then(response => {
          const data = response.data
          this.loginData = data
          this.recoveryCodes = data.recoveryCodes
          this.showRecoveryCodes = true
          this.$store.userAuthenticated(this.loginData, null, () => {})
        }).catch(error => {
          const data = error.response?.data
          if (data?.totp_failure) {
            error.response.data = { code: data.totp_failure }
          }
          this.setFromApiErrorResponse(error)
          if (!this.$store.canHandleNetworkError(error)) {
            this.$log.error('Error confirming 2FA setup: ' + error.message, error)
          }
        }).then(() => {
          this.isSaving = false
        })
      },
      finishSetup () {
        this.$store.userAuthenticated(this.loginData, null, () => {
          this.$router.push(this.$store.homePagePath)
        })
      }
    },
    created () {
      this.loadData()
    }
  }
</script>

<style scoped>
.card {
  border: none;
}
</style>
